import React, { useState } from "react";
import {
  CalendarDays,
  Clock,
  Users,
  ChevronDown,
  AlertCircle,
} from "lucide-react";
import { Link } from "react-router-dom";

const Reservation = () => {
  // =========================================
  // RESERVATION STATES
  // =========================================
  const [date, setDate] = useState("");
  const [time, setTime] = useState("19:00");
  const [people, setPeople] = useState(2);

  // =========================================
  // ERROR MESSAGE STATE
  // =========================================
  const [error, setError] = useState("");

  // =========================================
  // TIME OPTIONS
  // =========================================
  const timeOptions = [
    "17:00",
    "17:30",
    "18:00",
    "18:30",
    "19:00",
    "19:30",
    "20:00",
    "20:30",
    "21:00",
    "21:30",
    "22:00",
  ];

  // =========================================
  // TODAY'S DATE
  // =========================================
  const today = new Date();

  const todayString = `${today.getFullYear()}-${String(
    today.getMonth() + 1
  ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  // =========================================
  // FORMAT DATE
  // =========================================
  const formatDate = (dateString) => {
    if (!dateString) return "Select Date";

    const selectedDate = new Date(`${dateString}T00:00:00`);

    return selectedDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // =========================================
  // FORMAT TIME
  // =========================================
  const formatTime = (timeString) => {
    if (!timeString) return "Select Time";

    const [hours, minutes] = timeString.split(":");
    const dateObj = new Date();

    dateObj.setHours(Number(hours));
    dateObj.setMinutes(Number(minutes));

    return dateObj.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // =========================================
  // DATE CHANGE
  // =========================================
  const handleDateChange = (e) => {
    setDate(e.target.value);

    // Remove error when date is selected
    if (e.target.value) {
      setError("");
    }
  };

  // =========================================
  // TIME CHANGE
  // =========================================
  const handleTimeChange = (e) => {
    setTime(e.target.value);

    // Remove error when time is selected
    if (e.target.value) {
      setError("");
    }
  };

  // =========================================
  // OPEN OPENTABLE
  // =========================================
  const handleFindTable = () => {
    // Clear previous error
    setError("");

    // Validate date
    if (!date) {
      setError("Please select a date before finding a table.");
      return;
    }

    // Validate time
    if (!time) {
      setError("Please select a time before finding a table.");
      return;
    }

    // OpenTable expects:
    // YYYY-MM-DDTHH:MM
    const dateTime = `${date}T${time}`;

    const openTableUrl =
      `https://www.opentable.com/booking/restref/availability` +
      `?rid=1080589` +
      `&lang=en-US` +
      `&color=1` +
      `&r3uid=cfe` +
      `&dark=false` +
      `&correlationId=a6dc6ac7-f1b0-4935-968d-b7e8be23d0fb` +
      `&restRef=1080589` +
      `&partySize=${people}` +
      `&dateTime=${encodeURIComponent(dateTime)}` +
      `&otSource=Restaurant%20website`;

    // Open OpenTable
    window.open(openTableUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-black text-white">

      {/* =========================================
          MAIN RESERVATION SECTION
      ========================================= */}
      <main className="relative overflow-hidden">

        {/* Background decoration */}
        <div className="pointer-events-none absolute left-1/2 top-0 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-white/[0.025] blur-3xl" />

        <div className="relative mx-auto w-full max-w-7xl px-5 py-14 sm:px-8 sm:py-20 lg:px-10 lg:py-24">

          {/* =====================================
              PAGE TITLE
          ===================================== */}
          <div className="mx-auto mb-12 max-w-4xl text-center sm:mb-16">

            <h1 className="font-sans text-4xl font-black uppercase leading-[0.95] tracking-[-0.04em] text-white sm:text-5xl md:text-6xl lg:text-7xl">
              Reserve A Table
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-sm leading-6 text-gray-400 sm:text-base sm:leading-7 lg:text-lg">
              Choose your preferred date, time and number of guests.
              We'll take you directly to our reservation table to complete
              your booking.
            </p>

          </div>

          {/* =====================================
              RESERVATION LAYOUT
          ===================================== */}
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 lg:grid-cols-[1fr_420px] lg:items-stretch">

            {/* =====================================
                INFORMATION CARD
            ===================================== */}
            <div className="flex flex-col justify-between border border-white/10 bg-[#0b0b0b] p-6 sm:p-8 lg:p-10">

              <div>

                <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-gray-500">
                  Reservation Information
                </p>

                <h2 className="max-w-xl text-3xl font-black uppercase leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
                  Good food.
                  <br />
                  Great moments.
                </h2>

                <div className="mt-8 h-px w-full bg-white/10" />

                <div className="mt-8 space-y-6">

                  {/* INFO ITEM */}
                  <div>
                    <h3 className="mb-2 text-sm font-bold uppercase tracking-wider text-white">
                      Online Reservations
                    </h3>

                    <p className="text-sm leading-6 text-gray-400 sm:text-base">
                      Reservations for parties of 4 or less can be made
                      online using the reservation system.
                    </p>
                  </div>

                  {/* INFO ITEM */}
                  <div>
                    <h3 className="mb-2 text-sm font-bold uppercase tracking-wider text-white">
                      Larger Groups
                    </h3>

                    <p className="text-sm leading-6 text-gray-400 sm:text-base">
                      For reservations of 5-8 people, please contact our
                      team directly for assistance.
                    </p>
                  </div>

                  {/* INFO ITEM */}
                  <div>
                    <h3 className="mb-2 text-sm font-bold uppercase tracking-wider text-white">
                      Please Note
                    </h3>

                    <p className="text-sm leading-6 text-gray-400 sm:text-base">
                      If no table options are shown for your selected time,
                      that time may already be fully booked.
                    </p>
                  </div>

                </div>

              </div>

              {/* BOTTOM TEXT */}
              <div className="mt-10 border-t border-white/10 pt-6">
                <p className="text-xs uppercase tracking-[0.2em] text-gray-600">
                  Reservations powered by OpenTable
                </p>
              </div>

            </div>

            {/* =====================================
                RESERVATION CARD
            ===================================== */}
            <div className="border border-white/10 bg-white p-5 text-black shadow-2xl sm:p-7 lg:p-8">

              <div className="mb-7">
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.25em] text-gray-500">
                  Book Your Table
                </p>

                <h2 className="text-2xl font-black uppercase tracking-tight text-black sm:text-3xl">
                  Make A Reservation
                </h2>
              </div>

              {/* =================================
                  ERROR MESSAGE
              ================================= */}
              {error && (
                <div
                  role="alert"
                  className="mb-5 flex items-start gap-3 border border-red-200 bg-red-50 px-4 py-4 text-red-700"
                >
                  <AlertCircle
                    size={20}
                    className="mt-0.5 shrink-0"
                  />

                  <div>
                    <p className="text-sm font-bold">
                      Reservation Error
                    </p>

                    <p className="mt-1 text-sm leading-5 text-red-600">
                      {error}
                    </p>
                  </div>
                </div>
              )}

              {/* =================================
                  DATE
              ================================= */}
              <div className="relative mb-3">

                <CalendarDays
                  size={22}
                  className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-black"
                />

                <input
                  type="date"
                  value={date}
                  min={todayString}
                  onChange={handleDateChange}
                  className={`h-14 w-full cursor-pointer appearance-none border bg-white px-12 text-base font-medium text-black outline-none transition focus:border-black ${
                    error && !date
                      ? "border-red-400"
                      : "border-gray-300"
                  }`}
                />

              </div>

              {/* =================================
                  TIME
              ================================= */}
              <div className="relative">

                <Clock
                  size={22}
                  className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-black"
                />

                <select
                  value={time}
                  onChange={handleTimeChange}
                  className="h-14 w-full cursor-pointer appearance-none border-x border-b border-gray-300 bg-white px-12 pr-12 text-base font-medium text-black outline-none transition focus:border-black"
                >
                  {timeOptions.map((timeOption) => (
                    <option key={timeOption} value={timeOption}>
                      {formatTime(timeOption)}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={18}
                  className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-black"
                />

              </div>

              {/* =================================
                  PEOPLE
              ================================= */}
              <div className="relative">

                <Users
                  size={22}
                  className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-black"
                />

                <select
                  value={people}
                  onChange={(e) => setPeople(Number(e.target.value))}
                  className="h-14 w-full cursor-pointer appearance-none border-x border-b border-gray-300 bg-white px-12 pr-12 text-base font-medium text-black outline-none transition focus:border-black"
                >
                  {Array.from(
                    { length: 20 },
                    (_, index) => index + 1
                  ).map((number) => (
                    <option key={number} value={number}>
                      {number}{" "}
                      {number === 1 ? "person" : "people"}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={18}
                  className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-black"
                />

              </div>

              {/* =================================
                  SELECTED DETAILS
              ================================= */}
              <div className="mt-5 border border-gray-200 bg-gray-50 p-4 text-center">

                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Your Selection
                </p>

                <div className="mt-2 text-sm font-bold text-black sm:text-base">

                  <span>
                    {date ? formatDate(date) : "Select Date"}
                  </span>

                  <span className="mx-2 text-gray-400">
                    •
                  </span>

                  <span>
                    {formatTime(time)}
                  </span>

                  <span className="mx-2 text-gray-400">
                    •
                  </span>

                  <span>
                    {people}{" "}
                    {people === 1 ? "person" : "people"}
                  </span>

                </div>

              </div>

              {/* =================================
                  FIND TABLE BUTTON
              ================================= */}
              <button
                type="button"
                onClick={handleFindTable}
                className="mt-5 h-14 w-full bg-black px-6 text-sm font-bold uppercase tracking-[0.18em] text-white transition duration-300 hover:bg-gray-800 active:scale-[0.99]"
              >
                Find A Table
              </button>

              <p className="mt-4 text-center text-xs leading-5 text-gray-500">
                You will be redirected to OpenTable to view available
                tables and complete your reservation.
              </p>

            </div>

          </div>

        </div>
      </main>

      {/* =========================================
          FOOTER
      ========================================= */}
      <footer className="border-t border-white/10 bg-black">

        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-5 py-8 sm:px-8 md:flex-row lg:px-10">

          <p className="text-xs uppercase tracking-[0.2em] text-gray-600">
            © {new Date().getFullYear()} Capeesh
          </p>

          <Link
            to="/"
            className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 transition hover:text-white"
          >
            Back To Home
          </Link>

        </div>

      </footer>

    </div>
  );
};

export default Reservation;